import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PricingTeaser } from '../homepage/PricingTeaser';
import { WordBudgetCalculator } from './WordBudgetCalculator';
import { Footer } from '../homepage/Footer';
import { PageSeo } from '../seo/PageSeo';
import { productSchema, breadcrumbSchema } from '../seo/schemas';
import { useUser } from '../../context/UserContext';
import { useAuthUI } from '../../context/AuthUIContext';
import { trackEvent } from '../../utils/analytics';
import { PricingPageMessages as M, PricingPageText as T } from './PricingPage.messages';
import '../pages/static-page.css';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const { openSignIn } = useAuthUI();
  const { t } = useTranslation();
  const isAuthenticated = !!user.email;

  return (
    <>
      <PageSeo
        path="/pricing"
        localized
        titleKey="seo.pricing.title"
        descriptionKey="seo.pricing.description"
        ogImage="/og/og-pricing.png"
        keywords={['localization pricing', 'i18n SaaS pricing', 'AI translation cost']}
        jsonLd={[
          productSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Pricing', path: '/pricing' },
          ]),
        ]}
      />
      <div className="page-wrapper">
        {/*
          The page-level h1. PricingTeaser only carries an h2 for its plan grid,
          which left /pricing with no h1 at all — an SEO and screen-reader defect
          on the page where the buying decision happens.
        */}
        <section className="pt-page-hero">
          <div className="container">
            <h1>{t(M.HEADING, { defaultValue: T.HEADING })}</h1>
            <p className="pt-page-lede">{t(M.LEDE, { defaultValue: T.LEDE })}</p>
            <p className="pt-page-lede">{t(M.LEDE_FREE, { defaultValue: T.LEDE_FREE })}</p>
          </div>
        </section>
        {/*
          Picking a plan now goes somewhere. An anonymous visitor needs an account
          before they can be charged, so the choice opens registration — which lands
          on /get-started, since a new account has nothing to bill yet. A signed-in
          visitor goes to /billing with the plan named in the URL, rather than
          starting a charge from a link.
        */}
        <PricingTeaser
          onSeePricing={() => navigate(isAuthenticated ? '/dashboard' : '/get-started')}
          onSelectPlan={planKey => {
            trackEvent('pricing_plan_selected', { plan: planKey, authenticated: isAuthenticated });
            if (!isAuthenticated) {
              openSignIn('signUp');
              return;
            }
            navigate(planKey === 'trial' ? '/get-started' : `/billing?plan=${planKey}`);
          }}
        />
        {/*
          Below the plans on purpose: the plans answer "what does it cost", and this
          answers the question they raise, which is "which one do I actually need".
          Usage is source words x locales, so the answer depends on a number the
          table cannot show.
        */}
        <WordBudgetCalculator />
        <Footer />
      </div>
    </>
  );
};

export default PricingPage;
